import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { RotateCw } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";

const TIME_FILTERS = [
  { value: "24h", label: "Últimas 24h", hours: 24 },
  { value: "48h", label: "Últimas 48h", hours: 48 },
  { value: "7d", label: "Últimos 7 dias", hours: 24 * 7 },
] as const;

type TimeframeValue = (typeof TIME_FILTERS)[number]["value"];

function resolveSince(hours: number) {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
  } catch {
    return value as string;
  }
}

export function APILogTab() {
  const { tenantId, isAdminSaas } = useTenant();
  const [timeframe, setTimeframe] = useState<TimeframeValue>("24h");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [entityFilter, setEntityFilter] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");

  const selectedFilter = useMemo(
    () => TIME_FILTERS.find((f) => f.value === timeframe) ?? TIME_FILTERS[0],
    [timeframe],
  );

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "api-access-logs",
      isAdminSaas ? selectedTenantId : tenantId,
      timeframe,
      statusFilter,
      entityFilter,
      actionFilter,
      search,
      isAdminSaas ? "admin" : "tenant",
    ],
    enabled: isAdminSaas ? true : Boolean(tenantId),
    queryFn: async () => {
      const since = resolveSince(selectedFilter.hours);
      let query = supabase
        .from("api_access_logs" as any)
        .select("id, created_at, key_prefix, key_id, tenant_id, profile_id, entity, action, resource_id, status, error, ip, ua")
        .order("created_at", { ascending: false })
        .gte("created_at", since) as any;

      if (!isAdminSaas && tenantId) {
        query = query.eq("tenant_id", tenantId);
      }
      if (isAdminSaas && selectedTenantId) {
        query = query.eq("tenant_id", selectedTenantId);
      }
      if (statusFilter) query = query.eq("status", Number(statusFilter));
      if (entityFilter) query = query.eq("entity", entityFilter);
      if (actionFilter) query = query.eq("action", actionFilter);
      if (search) {
        query = query.or(
          `key_prefix.ilike.%${search}%,resource_id.ilike.%${search}%,error.ilike.%${search}%`,
        );
      }

      query = query.limit(200);

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: tenants } = useQuery({
    queryKey: ["api-access-logs-tenants"],
    enabled: isAdminSaas,
    queryFn: async () => {
      const { data, error } = await supabase.from("tenants" as any).select("id, name, slug").order("name", { ascending: true }) as any;
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Nenhum tenant selecionado</CardTitle>
          <CardDescription>Vincule-se a um tenant para visualizar os logs de API.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Logs de API</CardTitle>
              <CardDescription>Últimos eventos registrados em `api_access_logs`.</CardDescription>
            </div>
            <Button variant="outline" onClick={() => refetch()} disabled={isFetching} className="flex items-center gap-2">
              <RotateCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              {isFetching ? "Atualizando..." : "Atualizar"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {isAdminSaas && (
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Tenant</Label>
                <Select value={selectedTenantId} onValueChange={(value: string) => setSelectedTenantId(value === "all" ? "" : value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos os tenants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tenants</SelectItem>
                    {(tenants ?? []).map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Período</Label>
              <Select value={timeframe} onValueChange={(value: TimeframeValue) => setTimeframe(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_FILTERS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Status</Label>
              <Input placeholder="ex: 200" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Entidade</Label>
              <Input placeholder="ex: products" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Ação</Label>
              <Input placeholder="ex: read/create/update/delete" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Busca</Label>
              <Input placeholder="prefixo, resource id, erro" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Chave</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Entidade</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Recurso</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                    </TableRow>
                  ))
                ) : (data?.length ?? 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                      Nenhum log encontrado no período.
                    </TableCell>
                  </TableRow>
                ) : (
                  data!.map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-mono text-xs">{row.key_prefix ?? "-"}</span>
                          {row.key_id && <span className="text-xs text-muted-foreground">{row.key_id}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.profile_id ? (
                          <Badge variant="outline" className="text-xs">{row.profile_id}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="capitalize">{row.entity}</TableCell>
                      <TableCell className="uppercase text-xs">{row.action}</TableCell>
                      <TableCell className="font-mono text-xs break-words">{row.resource_id ?? "-"}</TableCell>
                      <TableCell className="text-right font-medium">{row.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="rounded-md border p-3">
            <p className="text-sm font-medium text-muted-foreground">Erros recentes</p>
            <div className="mt-2 space-y-2">
              {isLoading ? (
                <Skeleton className="h-4 w-full" />
              ) : (data ?? []).filter((row: any) => !!row.error).length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum erro registrado no período.</p>
              ) : (
                (data ?? []).filter((row: any) => !!row.error).slice(0, 5).map((row: any) => (
                  <div key={row.id} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDateTime(row.created_at)}</span>
                      <Badge variant="destructive">{row.status}</Badge>
                    </div>
                    <p className="mt-2 text-xs break-words">{row.error}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
